import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Send, MessageSquare, Hash, Search, Plus, User, Pin, Trash2,
  Paperclip, FileText, Download, ChevronDown, X, Users, SmilePlus
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(isoStr, t) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return t ? t("time.minutes") : "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

const EMOJI_LIST = [
  { key: "thumbsup", label: "\uD83D\uDC4D" },
  { key: "heart", label: "\u2764\uFE0F" },
  { key: "check", label: "\u2705" },
  { key: "eyes", label: "\uD83D\uDC40" },
  { key: "fire", label: "\uD83D\uDD25" },
];

function roleBadge(role) {
  const map = {
    owner: "bg-violet-100 text-violet-700",
    management: "bg-violet-100 text-violet-700",
    engineer: "bg-blue-100 text-blue-700",
    accountant: "bg-emerald-100 text-emerald-700",
    contractor: "bg-amber-100 text-amber-700",
    sales: "bg-pink-100 text-pink-700",
    hr: "bg-orange-100 text-orange-700",
  };
  return map[role] || "bg-slate-100 text-slate-600";
}

function ChannelIcon({ type }) {
  if (type === "direct") return <User className="w-4 h-4 text-slate-400" />;
  return <Hash className="w-4 h-4 text-slate-400" />;
}

// ── Search Modal ──
function SearchPanel({ onClose, onSelectChannel, t }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const doSearch = async () => {
    if (query.length < 2) return;
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/team-talk/search?q=${encodeURIComponent(query)}`);
      setResults(data);
    } catch { toast.error("Search failed"); }
    finally { setSearching(false); }
  };

  return (
    <div className="absolute inset-0 z-50 bg-black/30 flex items-start justify-center pt-20" onClick={onClose}>
      <div className="bg-white rounded-sm border border-slate-200 shadow-xl w-full max-w-xl mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-3 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            data-testid="search-messages-input"
            autoFocus className="flex-1 outline-none text-sm" placeholder={t("teamTalk.searchMessages")}
            value={query} onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && doSearch()}
          />
          <Button size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {searching && <p className="text-center text-sm text-slate-400 py-4">{t("teamTalk.searching")}</p>}
          {!searching && results.length === 0 && query.length >= 2 && (
            <p className="text-center text-sm text-slate-400 py-4">{t("teamTalk.noResults")}</p>
          )}
          {results.map(r => (
            <button key={r.id} data-testid={`search-result-${r.id}`}
              className="w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors"
              onClick={() => { onSelectChannel(r.channel_id); onClose(); }}>
              <p className="text-[11px] text-slate-400">{r.channel_name} - {r.sender_name}</p>
              <p className="text-sm text-slate-800 mt-0.5">{r.text}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(r.created_at)}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Create Channel Modal ──
function CreateChannelModal({ onClose, onCreated, allUsers, t }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [creating, setCreating] = useState(false);

  const toggleMember = (id) => {
    setSelectedMembers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/team-talk/channels`, {
        name: name.trim(), description: desc.trim(), channel_type: "general", member_ids: selectedMembers
      });
      onCreated(data);
      onClose();
      toast.success(t("teamTalk.channelCreated"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  return (
    <div className="absolute inset-0 z-50 bg-black/30 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-sm border border-slate-200 shadow-xl w-full max-w-md mx-4 p-5" onClick={e => e.stopPropagation()}>
        <h3 className="font-semibold text-slate-900 text-base mb-4">{t("teamTalk.createChannel")}</h3>
        <Input data-testid="create-channel-name" placeholder={t("teamTalk.channelName")} value={name} onChange={e => setName(e.target.value)} className="mb-3 rounded-sm" />
        <Input data-testid="create-channel-desc" placeholder={t("teamTalk.descriptionOptional")} value={desc} onChange={e => setDesc(e.target.value)} className="mb-4 rounded-sm" />
        <p className="text-xs font-medium text-slate-500 mb-2">{t("teamTalk.addMembers")}</p>
        <div className="max-h-36 overflow-y-auto space-y-1 mb-4 border border-slate-100 rounded-sm p-2">
          {allUsers.map(u => (
            <label key={u.id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-slate-50 cursor-pointer text-sm">
              <input type="checkbox" checked={selectedMembers.includes(u.id)} onChange={() => toggleMember(u.id)} className="rounded" />
              <span>{u.name}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${roleBadge(u.role)}`}>{u.role}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={onClose} className="rounded-sm">{t("teamTalk.cancel")}</Button>
          <Button size="sm" onClick={handleCreate} disabled={creating || !name.trim()}
            data-testid="create-channel-submit" className="rounded-sm bg-indigo-600 hover:bg-indigo-700">{t("teamTalk.create")}</Button>
        </div>
      </div>
    </div>
  );
}

// ── Message Bubble ──
function MessageBubble({ msg, isMe, user, onReact, onPin, onDelete, onReply, t }) {
  const [showActions, setShowActions] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);

  const isImage = msg.file_url && /\.(jpg|jpeg|png|gif|webp)$/i.test(msg.file_name || "");

  return (
    <div
      data-testid={`message-${msg.id}`}
      className={`group flex ${isMe ? "justify-end" : "justify-start"}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => { setShowActions(false); setShowEmoji(false); }}
    >
      <div className={`relative max-w-[70%] ${isMe ? "order-2" : ""}`}>
        {/* Actions toolbar */}
        {showActions && (
          <div className={`absolute -top-7 ${isMe ? "right-0" : "left-0"} flex gap-0.5 bg-white border border-slate-200 rounded-sm shadow-sm z-10`}>
            <button onClick={() => setShowEmoji(!showEmoji)} className="p-1 hover:bg-slate-100 rounded-sm" title="React">
              <SmilePlus className="w-3.5 h-3.5 text-slate-500" />
            </button>
            <button onClick={() => onReply(msg)} className="p-1 hover:bg-slate-100 rounded-sm" title="Reply">
              <MessageSquare className="w-3.5 h-3.5 text-slate-500" />
            </button>
            <button onClick={() => onPin(msg.id)} className="p-1 hover:bg-slate-100 rounded-sm" title="Pin">
              <Pin className="w-3.5 h-3.5 text-slate-500" />
            </button>
            {(isMe || user.role === "owner") && (
              <button onClick={() => onDelete(msg.id)} className="p-1 hover:bg-red-50 rounded-sm" title="Delete">
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </button>
            )}
          </div>
        )}

        {/* Emoji picker */}
        {showEmoji && (
          <div className={`absolute -top-14 ${isMe ? "right-0" : "left-0"} flex gap-1 bg-white border border-slate-200 rounded-sm shadow-md p-1.5 z-20`}>
            {EMOJI_LIST.map(e => (
              <button key={e.key} onClick={() => { onReact(msg.id, e.key); setShowEmoji(false); }}
                className="text-base hover:scale-125 transition-transform px-1">{e.label}</button>
            ))}
          </div>
        )}

        <div className={`px-3 py-2 rounded-sm border text-sm ${
          isMe ? "bg-indigo-50 border-indigo-200 rounded-br-none" : "bg-white border-slate-200 rounded-bl-none"
        }`}>
          {!isMe && (
            <p className="text-[10px] font-semibold mb-1">
              <span className="text-indigo-600">{msg.sender_name}</span>
              <span className={`ml-1.5 px-1 py-0.5 rounded-sm ${roleBadge(msg.sender_role)}`}>{msg.sender_role}</span>
            </p>
          )}

          {msg.reply_to_text && (
            <div className="text-[11px] text-slate-400 border-l-2 border-indigo-300 pl-2 mb-1.5 italic truncate">
              {msg.reply_to_text}
            </div>
          )}

          {msg.pinned && (
            <div className="flex items-center gap-1 text-[10px] text-amber-600 mb-1">
              <Pin className="w-3 h-3" /> {t("teamTalk.pinned")}
            </div>
          )}

          {isImage ? (
            <div>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`} alt={msg.file_name}
                className="max-w-full max-h-48 rounded-sm mb-1 cursor-pointer"
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`, "_blank")} />
              {msg.text !== msg.file_name && <p className="leading-snug">{msg.text}</p>}
            </div>
          ) : msg.file_url ? (
            <a href={`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-sm hover:bg-slate-100 transition-colors mb-1">
              <FileText className="w-5 h-5 text-indigo-500 flex-shrink-0" />
              <span className="text-xs truncate flex-1">{msg.file_name}</span>
              <Download className="w-4 h-4 text-slate-400" />
            </a>
          ) : null}

          {!msg.file_url && <p className="leading-snug whitespace-pre-wrap">{msg.text}</p>}

          <p className="text-[10px] text-slate-400 mt-1 text-right">{timeAgo(msg.created_at)}</p>
        </div>

        {/* Reactions */}
        {msg.reactions && Object.keys(msg.reactions).length > 0 && (
          <div className="flex gap-1 mt-1 flex-wrap">
            {Object.entries(msg.reactions).map(([emoji, userIds]) => {
              const emojiLabel = EMOJI_LIST.find(e => e.key === emoji)?.label || emoji;
              const hasReacted = userIds.includes(user.id);
              return (
                <button key={emoji} onClick={() => onReact(msg.id, emoji)}
                  className={`text-xs px-1.5 py-0.5 rounded-full border ${hasReacted ? "bg-indigo-50 border-indigo-300" : "bg-slate-50 border-slate-200"} hover:bg-indigo-100 transition-colors`}>
                  {emojiLabel} {userIds.length}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main TeamTalk ──
export default function TeamTalkPage({ user, onLogout }) {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(() => new URLSearchParams(window.location.search).get("channelId"));
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showNewDM, setShowNewDM] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [channelFilter, setChannelFilter] = useState("all"); // all, project, direct, general
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load channels
  const loadChannels = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/team-talk/channels`);
      setChannels(data);
    } catch {}
  }, []);

  // Load messages for selected channel
  const loadMessages = useCallback(async () => {
    if (!selectedChannel) return;
    try {
      const { data } = await axios.get(`${API}/team-talk/channels/${selectedChannel}/messages`);
      setMessages(data);
    } catch {}
  }, [selectedChannel]);

  // Load all users for DMs / channel creation
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users`);
        setAllUsers(data.filter(u => u.id !== user.id));
      } catch {}
    })();
    loadChannels();
  }, [loadChannels, user.id]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Polling
  useEffect(() => {
    const i1 = setInterval(loadChannels, 12000);
    const i2 = setInterval(loadMessages, 6000);
    return () => { clearInterval(i1); clearInterval(i2); };
  }, [loadChannels, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const selectChannel = (chId) => {
    setSelectedChannel(chId);
    setMessages([]);
    setReplyTo(null);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim() || !selectedChannel) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/team-talk/channels/${selectedChannel}/messages`, {
        text: text.trim(), channel_id: selectedChannel, reply_to: replyTo?.id || null
      });
      if (replyTo) data.reply_to_text = replyTo.text?.substring(0, 80);
      setMessages(prev => [...prev, data]);
      setText("");
      setReplyTo(null);
      loadChannels();
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const uploadFile = async (file) => {
    if (!selectedChannel || !file) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("text", "");
      const { data } = await axios.post(`${API}/team-talk/channels/${selectedChannel}/upload`, form);
      setMessages(prev => [...prev, data]);
      loadChannels();
      toast.success("File shared");
    } catch { toast.error("Upload failed"); }
    finally { setSending(false); }
  };

  const handleReact = async (msgId, emoji) => {
    try {
      const { data } = await axios.post(`${API}/team-talk/messages/${msgId}/react`, { emoji });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, reactions: data.reactions } : m));
    } catch {}
  };

  const handlePin = async (msgId) => {
    try {
      const { data } = await axios.post(`${API}/team-talk/messages/${msgId}/pin`);
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, pinned: data.pinned } : m));
      toast.success(data.pinned ? t("teamTalk.messagePinned") : t("teamTalk.messageUnpinned"));
    } catch {}
  };

  const handleDelete = async (msgId) => {
    try {
      await axios.delete(`${API}/team-talk/messages/${msgId}`);
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, deleted: true, text: "Message deleted" } : m));
    } catch {}
  };

  const startDM = async (targetUser) => {
    try {
      const { data } = await axios.post(`${API}/team-talk/dm`, { user_id: targetUser.id });
      await loadChannels();
      setSelectedChannel(data.id);
      setShowNewDM(false);
      toast.success(`DM with ${targetUser.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const selectedChObj = channels.find(c => c.id === selectedChannel);
  const filteredChannels = channels.filter(c => channelFilter === "all" || c.channel_type === channelFilter);

  const getDMName = (ch) => {
    if (ch.channel_type !== "direct") return ch.name;
    const otherMember = allUsers.find(u => ch.members.includes(u.id));
    return otherMember ? otherMember.name : ch.name.replace("DM: ", "").replace(` & ${user.name}`, "").replace(`${user.name} & `, "");
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-3" data-testid="team-talk-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">{t("teamTalk.title")}</h1>
            <p className="text-slate-500 text-sm mt-1">{t("teamTalk.subtitle")}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setShowSearch(true)} data-testid="search-btn">
              <Search className="w-4 h-4 mr-1" /> {t("teamTalk.search")}
            </Button>
          </div>
        </div>

        <div className="flex h-[calc(100vh-180px)] bg-white border border-slate-200 rounded-sm overflow-hidden">
          {/* ── Sidebar ── */}
          <div className="w-72 border-r border-slate-200 flex flex-col flex-shrink-0">
            <div className="px-3 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <p className="font-semibold text-slate-900 text-sm">{t("teamTalk.channels")}</p>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-sm" title="New DM"
                  onClick={() => setShowNewDM(true)} data-testid="new-dm-btn">
                  <User className="w-3.5 h-3.5" />
                </Button>
                {user.role !== "contractor" && (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-sm" title="New Channel"
                    onClick={() => setShowCreateChannel(true)} data-testid="new-channel-btn">
                    <Plus className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>

            {/* Filter tabs */}
            <div className="flex border-b border-slate-100">
              {[
                { key: "all", label: t("teamTalk.all") },
                { key: "project", label: t("teamTalk.project") },
                { key: "direct", label: t("teamTalk.dms") },
                { key: "general", label: t("teamTalk.general") },
              ].map(f => (
                <button key={f.key}
                  onClick={() => setChannelFilter(f.key)}
                  className={`flex-1 text-[11px] py-1.5 transition-colors ${
                    channelFilter === f.key ? "bg-indigo-50 text-indigo-700 font-medium border-b-2 border-indigo-600" : "text-slate-500 hover:bg-slate-50"
                  }`}
                  data-testid={`filter-${f.key}`}
                >{f.label}</button>
              ))}
            </div>

            {/* Channel list */}
            <div className="flex-1 overflow-y-auto" data-testid="channel-list">
              {filteredChannels.length === 0 && (
                <div className="flex flex-col items-center justify-center h-24 text-slate-400 text-xs p-3 text-center">
                  <MessageSquare className="w-6 h-6 mb-1 text-slate-200" />
                  {t("teamTalk.noChannels")}
                </div>
              )}
              {filteredChannels.map(ch => (
                <button key={ch.id}
                  onClick={() => selectChannel(ch.id)}
                  data-testid={`channel-${ch.id}`}
                  className={`w-full text-left px-3 py-2.5 border-b border-slate-50 transition-colors ${
                    selectedChannel === ch.id ? "bg-indigo-50 border-l-2 border-l-indigo-600" : "hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <ChannelIcon type={ch.channel_type} />
                    <span className="text-sm font-medium text-slate-800 truncate flex-1">{getDMName(ch)}</span>
                    {ch.unread_count > 0 && (
                      <span className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                        {ch.unread_count}
                      </span>
                    )}
                  </div>
                  {ch.last_message && (
                    <p className="text-[11px] text-slate-400 truncate mt-0.5 pl-6">{ch.last_message.sender_name}: {ch.last_message.text}</p>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* ── Chat area ── */}
          <div className="flex-1 flex flex-col min-w-0">
            {!selectedChannel ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                <MessageSquare className="w-14 h-14 mb-3 text-slate-200" />
                <p className="text-sm font-medium">{t("teamTalk.selectChannel")}</p>
                <p className="text-xs mt-1">{t("teamTalk.orCreateNew")}</p>
              </div>
            ) : (
              <>
                {/* Channel header */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex-shrink-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <ChannelIcon type={selectedChObj?.channel_type} />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-sm truncate" data-testid="channel-name">{selectedChObj ? getDMName(selectedChObj) : ""}</p>
                      {selectedChObj?.description && (
                        <p className="text-[11px] text-slate-400 truncate">{selectedChObj.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="text-[11px] text-slate-400 flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {selectedChObj?.members?.length || 0}
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" data-testid="messages-container">
                  {messages.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full text-slate-300">
                      <MessageSquare className="w-10 h-10 mb-2" />
                      <p className="text-sm">{t("teamTalk.noMessages")}</p>
                    </div>
                  )}
                  {messages.map(m => (
                    <MessageBubble key={m.id} msg={m} isMe={m.sender_id === user.id} user={user}
                      onReact={handleReact} onPin={handlePin} onDelete={handleDelete} onReply={setReplyTo} t={t} />
                  ))}
                  <div ref={bottomRef} />
                </div>

                {/* Reply preview */}
                {replyTo && (
                  <div className="px-4 py-1.5 bg-indigo-50 border-t border-indigo-100 flex items-center justify-between">
                    <p className="text-xs text-indigo-600 truncate flex-1">
                      {t("teamTalk.replyingTo")} <span className="font-semibold">{replyTo.sender_name}</span>: {replyTo.text?.substring(0, 60)}
                    </p>
                    <button onClick={() => setReplyTo(null)}><X className="w-3.5 h-3.5 text-indigo-400" /></button>
                  </div>
                )}

                {/* Input */}
                <form onSubmit={sendMessage} className="flex gap-2 items-center px-4 py-2.5 border-t border-slate-100 flex-shrink-0">
                  <input type="file" ref={fileInputRef} className="hidden" onChange={e => { if (e.target.files[0]) uploadFile(e.target.files[0]); e.target.value = ""; }} />
                  <Button type="button" variant="ghost" size="sm" className="rounded-sm h-8 w-8 p-0 flex-shrink-0"
                    onClick={() => fileInputRef.current?.click()} disabled={sending} data-testid="attach-file-btn">
                    <Paperclip className="w-4 h-4 text-slate-400" />
                  </Button>
                  <Input
                    data-testid="message-input"
                    className="flex-1 rounded-sm text-sm" placeholder={t("teamTalk.typeMessage")}
                    value={text} onChange={e => setText(e.target.value)} disabled={sending}
                  />
                  <Button type="submit" disabled={sending || !text.trim()}
                    data-testid="send-message-btn"
                    className="rounded-sm bg-indigo-600 hover:bg-indigo-700 px-3 h-8 flex-shrink-0">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {showSearch && <SearchPanel onClose={() => setShowSearch(false)} onSelectChannel={selectChannel} t={t} />}

      {showCreateChannel && (
        <CreateChannelModal allUsers={allUsers} onClose={() => setShowCreateChannel(false)}
          onCreated={(ch) => { loadChannels(); selectChannel(ch.id); }} t={t} />
      )}

      {showNewDM && (
        <div className="absolute inset-0 z-50 bg-black/30 flex items-center justify-center" onClick={() => setShowNewDM(false)}>
          <div className="bg-white rounded-sm border border-slate-200 shadow-xl w-full max-w-sm mx-4 p-5" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-900 text-base mb-3">{t("teamTalk.newDirectMessage")}</h3>
            <div className="max-h-64 overflow-y-auto space-y-1" data-testid="dm-user-list">
              {allUsers.map(u => (
                <button key={u.id} onClick={() => startDM(u)}
                  data-testid={`dm-user-${u.id}`}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-sm hover:bg-slate-50 text-left transition-colors">
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-semibold text-sm">
                    {u.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                    <p className={`text-[10px] px-1.5 py-0.5 rounded-sm inline-block ${roleBadge(u.role)}`}>{u.role}</p>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="outline" size="sm" onClick={() => setShowNewDM(false)} className="rounded-sm">{t("teamTalk.cancel")}</Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
